import { useEffect, useLayoutEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { ApiError } from "../../api/client";
import { homeFor, MIN_PASSWORD_LENGTH, type Challenge } from "../../api/auth";
import type { Me } from "../../api/types";
import { Notice } from "../../design/components";
import { useAuth } from "../../auth/AuthProvider";
import { toMe } from "../../auth/session";
import { AuthSplit } from "./AuthSplit";
import { ChallengeStep } from "./ChallengeStep";
import { useHubBranding } from "./useHubBranding";

export function strengthHint(password: string): string {
  const missing = MIN_PASSWORD_LENGTH - password.length;
  if (password.length === 0) return `At least ${MIN_PASSWORD_LENGTH} characters`;
  if (missing > 0) return `At least ${MIN_PASSWORD_LENGTH} characters; ${missing} more to go`;
  return "Long enough. Longer is stronger.";
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError && error.code === "invite_invalid") return "This invitation link is not valid";
  if (error instanceof ApiError && error.code === "invalid_credentials") {
    return "This invitation is for an existing account. Enter that account's password.";
  }
  if (error instanceof ApiError) return error.message;
  return "Something went wrong. Try again.";
}

/** m6-rulings.md #110: the one invitation landing route, on the portal surface for every role. */
export function AcceptInvite() {
  useHubBranding("hub");
  const { acceptInvite } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const nameRef = useRef<HTMLInputElement>(null);
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [expired, setExpired] = useState<string | null>(null);
  const [expiredHint, setExpiredHint] = useState("");
  const [pending, setPending] = useState(false);
  const ready = displayName.trim().length > 0 && password.length >= MIN_PASSWORD_LENGTH;

  useLayoutEffect(() => {
    const previous = document.body.className;
    document.body.className = "surface-portal";
    return () => {
      document.body.className = previous;
    };
  }, []);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  function finish(me: Me) {
    navigate(homeFor(me.surface), { replace: true });
  }

  // The invitation is already redeemed by now, so the way forward is the sign-in page, not this form.
  function expire(message: string) {
    setExpiredHint(
      challenge?.status === "mfa_enrolment_required"
        ? "Your account was created. Sign in to finish setting up your second factor."
        : "Your invitation was accepted. Sign in to continue.",
    );
    setChallenge(null);
    setExpired(message);
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready) return;
    setPending(true);
    setFailure(null);
    try {
      const outcome = await acceptInvite({ token, displayName: displayName.trim(), password });
      if (outcome.status === "ok") {
        finish(toMe(outcome));
        return;
      }
      setChallenge(outcome);
    } catch (error) {
      setFailure(messageFor(error));
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthSplit title="Accept your invitation" welcome="Create your account to get started.">
      {challenge ? <ChallengeStep challenge={challenge} onDone={finish} onExpired={expire} /> : null}
      {expired ? (
        <div className="card signin-card">
          <div className="card-body stack">
            <Notice kind="fail">{expired}</Notice>
            <p>{expiredHint}</p>
            <Link className="btn" to="/portal/signin">
              Go to sign in
            </Link>
          </div>
        </div>
      ) : null}
      {!challenge && !expired && !token ? (
        <div className="card signin-card">
          <div className="card-body">
            <Notice kind="info">Open the link from your invitation email to continue.</Notice>
          </div>
        </div>
      ) : null}
      {!challenge && !expired && token ? (
        <form className="card signin-card" onSubmit={onSubmit}>
          <div className="card-body stack">
            {failure ? <Notice kind="fail">{failure}</Notice> : null}
            <label className="field" htmlFor="accept-name">
              <span>Your name</span>
              <input
                id="accept-name"
                ref={nameRef}
                type="text"
                autoComplete="name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
              />
            </label>
            <label className="field" htmlFor="accept-password">
              <span>Choose a password</span>
              <input
                id="accept-password"
                type="password"
                autoComplete="new-password"
                aria-describedby="accept-password-hint"
                minLength={MIN_PASSWORD_LENGTH}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>
            <span id="accept-password-hint" className="field-hint">
              {strengthHint(password)}
            </span>
            <button className="btn" type="submit" disabled={pending || !ready}>
              Create my account
            </button>
          </div>
        </form>
      ) : null}
    </AuthSplit>
  );
}
